import EditableTable from '@/components/Table/EditableTable'
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { ApprovalStatus } from '@/features/offers/constants'
import {
  APPROVAL_TABLE_TYPE,
  buildApproverTableColumn,
  getInvitationButtonText,
} from '@/features/offers/offer-detail/helper/approverTableColumn.helper'
import { getDelegationRemark } from '@/features/offers/offer-detail/helper/delegationRemark.helper'
import useUser from '@/features/users/useUser'
import { authSelector } from '@/stores/auth/authSlice'
import { PRApprovalType } from '@eapproval/offer-shared'
import { createColumnHelper } from '@tanstack/react-table'
import React, { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

const TECHNICAL_APPROVER_TYPES = [PRApprovalType.TECHNICAL_3, PRApprovalType.TECHNICAL_PROJECT_MANAGER]

const GCPRApproverTable = ({ submissionIndex, canEditPR }) => {
  const approverDetails =
    useWatch({
      name: `sourcing.GCMaterialSubmissions.${submissionIndex}.approverDetails`,
    }) ?? []

  const columnHelper = createColumnHelper()
  const dispatch = useDispatch()
  const { useFetchAllUsers } = useUser()
  const { user } = useSelector(authSelector)

  const { setValue, getValues } = useFormContext()

  const { data: fetchUsersResponse } = useFetchAllUsers({
    page: 1,
    pageSize: 0,
    sortBy: 'fullName',
    orderBy: 1,
    filters: { status: 'active' },
  })
  const tableData = approverDetails.map((detail) => {
    let filteredUserOptions = []

    if (Array.isArray(fetchUsersResponse?.data)) {
      switch (detail.type) {
        case PRApprovalType.LOGISTIC:
          filteredUserOptions = fetchUsersResponse.data
            .filter((user) => user?.contributorGroup === ContributorGroupType.LOGISTIC)
            .map((user) => {
              return {
                label: user.fullName,
                value: user._id,
              }
            })
          break
        case PRApprovalType.LICENSE_MANAGER:
          filteredUserOptions = fetchUsersResponse.data
            .filter((user) => user?.contributorGroup === ContributorGroupType.LICENSE_MANAGER)
            .map((user) => {
              return {
                label: user.fullName,
                value: user._id,
              }
            })
          break
        case PRApprovalType.TECHNICAL_PROJECT_MANAGER:
          filteredUserOptions = fetchUsersResponse.data
            .filter((user) =>
              [ContributorGroupType.TECHNICAL_PROJECT_MANAGER, ContributorGroupType.SALES].includes(
                user.contributorGroup
              )
            )
            .map((user) => {
              return {
                label: user.fullName,
                value: user._id,
              }
            })
          break
        case PRApprovalType.COMMERCIAL_PROJECT_MANAGER:
          filteredUserOptions = fetchUsersResponse.data
            .filter((user) => [ContributorGroupType.COMMERCIAL_PROJECT_MANAGER].includes(user.contributorGroup))
            .map((user) => {
              return {
                label: user.fullName,
                value: user._id,
              }
            })
          break
        default:
          filteredUserOptions = fetchUsersResponse.data.map((user) => {
            return {
              label: user.fullName,
              value: user._id,
            }
          })
      }
    }

    const approverUserId = detail?.user?._id || detail?.user

    // Keep the assigned approver selectable + labeled even if not part of the
    // computed contributor-group options (e.g. they changed contributor group
    // since assignment), mirroring the fix applied to BookingTabApprovalSection.
    if (approverUserId && !filteredUserOptions.some((option) => option.value === approverUserId)) {
      const assignedUserFromList = Array.isArray(fetchUsersResponse?.data)
        ? fetchUsersResponse.data.find((candidate) => candidate._id === approverUserId)
        : undefined
      filteredUserOptions = [
        ...filteredUserOptions,
        {
          value: approverUserId,
          label: detail?.user?.fullName || assignedUserFromList?.fullName || approverUserId,
        },
      ]
    }

    const invitationButtonText = getInvitationButtonText(detail.invitedUsers)

    const { delegationRemark, isEffective } = TECHNICAL_APPROVER_TYPES.includes(detail.type)
      ? getDelegationRemark(detail?.delegationDetail, fetchUsersResponse?.data)
      : { delegationRemark: '', isEffective: true }

    return {
      ...detail,
      user: approverUserId,
      typeOptions: [{ label: detail.type, value: detail.type }],
      userOptions: filteredUserOptions,
      isEffective,
      delegationRemark,
      disableEditType: true,
      disableEditUser: false,
      disableEditDueDate: [ApprovalStatus.APPROVED, ApprovalStatus.REJECTED].includes(detail?.status),
      disableEditRemark: true,
      disableRemoveApprover: true,

      invitationButtonText: invitationButtonText,
      canManageInvitations: String(approverUserId || '') === String(user?._id || ''),
    }
  })

  const updateTableData = (rowIndex, columnId, value) => {
    setValue(`sourcing.GCMaterialSubmissions.${submissionIndex}.approverDetails.${rowIndex}.${columnId}`, value, {
      shouldDirty: true,
    })
  }

  const columnDef = useMemo(
    () =>
      buildApproverTableColumn({
        columnHelper,
        dispatch,
        tableType: APPROVAL_TABLE_TYPE.GCPR_APPROVAL,
        updateInvitationParameter: {
          offerId: getValues('_id'),
          submissionIndex,
        },
      }),
    [columnHelper, getValues, submissionIndex, dispatch]
  )

  return (
    <EditableTable
      columnDef={columnDef}
      data={tableData}
      updateData={updateTableData}
      disabled={!canEditPR}
      tableWidth="100%"
    />
  )
}

export default GCPRApproverTable
